import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import type { Permission } from "../../repositories/customPackage";
import {
  exclusivePermissionApi,
  type ExclusivePermissionGrant,
} from "../../repositories/exclusivePermission";
import {
  buildDefaultValues,
  isFieldInvalid,
  resolveLimit,
  valuesFromGrants,
  type PermissionValues,
  type PermissionFieldValue,
} from "../../utils/permissions";
import ExclusivePermissionsForm from "./ExclusivePermissionsForm";
import Button from "../ui/Button";
import { Skeleton } from "../ui/skeleton";


interface EditExclusivePermissionsModalProps {
  userId: number;
  userName?: string;
  userEmail?: string;
  permissions: Permission[];
  onClose: () => void;
  onSaved: () => void;
}

// Normalize the (unspecified-shape) GET grants response into a flat list of
// { permissionId, limit }. The backend schema isn't documented, so accept the
// common shapes defensively.
const extractGrants = (data: any): ExclusivePermissionGrant[] => {
  const raw =
    data?.data?.permissions ||
    data?.data?.grants ||
    data?.permissions ||
    data?.grants ||
    data?.data ||
    (Array.isArray(data) ? data : []);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((g: any) => ({
      permissionId: g.permissionId ?? g.permission_id,
      limit: g.limit ?? null,
    }))
    .filter((g: ExclusivePermissionGrant) => g.permissionId != null);
};

export default function EditExclusivePermissionsModal({
  userId,
  userName,
  userEmail,
  permissions,
  onClose,
  onSaved,
}: EditExclusivePermissionsModalProps) {
  const [values, setValues] = useState<PermissionValues>(() => buildDefaultValues(permissions));
  const [errors, setErrors] = useState<Record<number, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Snapshot of the values the modal opened with, to disable Save until changed.
  const originalValues = useRef<PermissionValues | null>(null);
  // Hold the latest permissions in a ref so the load effect can read them
  // without depending on the prop's identity (avoids a re-run / render loop if
  // the parent recreates the array).
  const permissionsRef = useRef(permissions);
  permissionsRef.current = permissions;

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setIsLoading(true);
        const response = await exclusivePermissionApi.getUserPermissions(userId);
        const grants = extractGrants(response.data);
        const seeded = valuesFromGrants(permissionsRef.current, grants);
        if (!active) return;
        setValues(seeded);
        originalValues.current = seeded;
      } catch (error: any) {
        if (!active) return;
        const seeded = buildDefaultValues(permissionsRef.current);
        setValues(seeded);
        originalValues.current = seeded;
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Failed to load the user's permissions",
        });
      } finally {
        if (active) setIsLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
    // Re-load only when the target user changes. `permissions` is read via a ref
    // (permissionsRef) so this effect doesn't re-run on every parent render.
  }, [userId]);

  const handleChange = (permissionId: number, value: PermissionFieldValue) => {
    setValues((prev) => ({ ...prev, [permissionId]: value }));
    if (errors[permissionId]) {
      setErrors((prev) => ({ ...prev, [permissionId]: false }));
    }
  };

  const isDirty =
    !!originalValues.current &&
    JSON.stringify(values) !== JSON.stringify(originalValues.current);

  const handleSave = async () => {
    // Validate numeric fields — flag invalid inputs with a red border.
    const nextErrors: Record<number, boolean> = {};
    let hasError = false;
    for (const permission of permissions) {
      const value = values[permission.id];
      if (value && isFieldInvalid(permission, value)) {
        nextErrors[permission.id] = true;
        hasError = true;
      }
    }
    if (hasError) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    // Build the updated-grants payload.
    const updatedData: ExclusivePermissionGrant[] = [];
    for (const permission of permissions) {
      const value = values[permission.id];
      if (!value) continue;
      const limit = resolveLimit(permission, value);
      if (limit === undefined) continue; // already guarded above
      updatedData.push({ permissionId: permission.id, limit });
    }

    try {
      setIsSaving(true);
      await exclusivePermissionApi.updatePermissions(userId, updatedData);
      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Exclusive permissions updated successfully",
      });
      onSaved();
      onClose();
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Failed to update permissions",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      {/*
        Flex column with a scrolling body: the permission list is backend-driven,
        so a long one has to scroll inside the panel rather than push the footer
        off the bottom of the viewport.
      */}
      <div className="w-full max-w-lg bg-white rounded-lg shadow-lg max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-black">Edit Exclusive Permissions</h2>
          {(userName || userEmail) && (
            <p className="mt-1 text-sm text-neutral-600">
              {[userName, userEmail].filter(Boolean).join(" — ")}
            </p>
          )}
        </div>

        <div className="flex-1 px-6 py-4 overflow-y-auto scrollbar-thin">
          {isLoading ? (
            // Shaped like the permission rows below, so the modal doesn't
            // resize when the grants land.
            <div className="space-y-3" aria-busy="true">
              {permissions.map((permission) => (
                <Skeleton key={permission.id} className="w-full h-[58px] rounded-lg" />
              ))}
            </div>
          ) : (
            <ExclusivePermissionsForm
              permissions={permissions}
              values={values}
              errors={errors}
              onChange={handleChange}
            />
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t rounded-b-lg border-neutral-200 bg-neutral-50">
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSaving} disabled={isLoading || !isDirty}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
